import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { campaignsApi } from '@/api/campaigns.api';
import { extractErrorMessage } from '@/lib/errors';
import type { CampaignStatus } from '@/types/api.types';

export function useCampaignsList(params?: { page?: number; pageSize?: number; status?: CampaignStatus }) {
  return useQuery({
    queryKey: ['campaigns', 'list', params],
    queryFn: () => campaignsApi.list(params),
    select: (res) => res.data,
  });
}

export function useCampaignDetail(id: string) {
  return useQuery({
    queryKey: ['campaigns', 'detail', id],
    queryFn: () => campaignsApi.get(id),
    select: (res) => res.data.data,
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: campaignsApi.create,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campanha criada com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useStartCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => campaignsApi.start(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaigns', 'detail', id] });
      toast.success('Campanha iniciada');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => campaignsApi.cancel(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaigns', 'detail', id] });
      toast.success('Campanha cancelada');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}
